import React, { useState, useEffect, useCallback } from "react";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

// feat_289 — Editor de feriados por empresa.
//   - Nacionales AR: toggle "trabajamos ese día" (national-override).
//   - Custom: feriados propios (recurrentes o fecha única).
// feat_415 (CK48) — cada feriado puede llevar ALCANCE ("solo Córdoba") y un
// MENSAJE propio para el cliente. En los nacionales se carga como "nota"
// (national-note) sin tocar si se trabaja o no.
// Consume /settings/holidays (isAuth + getTenantId; companyId del token).

const useStyles = makeStyles(theme => ({
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 4,
    padding: "4px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  rowMain: { minWidth: 0, flex: "1 1 220px" },
  rowName: { fontSize: 13, wordBreak: "break-word" },
  scope: {
    display: "inline-block",
    marginLeft: 6,
    padding: "0 6px",
    borderRadius: 10,
    fontSize: 11,
    lineHeight: "18px",
    background: theme.palette.type === "dark" ? "#2a3942" : "#e7f3ef",
    color: theme.palette.type === "dark" ? "#d1d7db" : "#075e54",
  },
  rowActions: { display: "flex", alignItems: "center", gap: 2, flexShrink: 0 },
  list: { maxHeight: 260, overflowY: "auto", marginBottom: 16 },
}));

const fmtDate = ymd => {
  if (!ymd || typeof ymd !== "string") return ymd;
  const [y, m, d] = ymd.split("-");
  if (!d) return ymd;
  return `${d}/${m}/${y}`;
};

const EMPTY_FORM = { date: "", name: "", recurring: false, scope: "", message: "" };

const HolidaysEditor = ({ onChanged }) => {
  const classes = useStyles();
  const year = new Date().getFullYear();
  const [nationals, setNationals] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [loading, setLoading] = useState(false);
  // dialog: null | {mode:"add"} | {mode:"edit", idx} | {mode:"national", date, name}
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/settings/holidays?year=${year}`);
      setNationals(data.nationals || []);
      setCustoms(data.customs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    if (onChanged) onChanged();
  };

  const toggleNationalWorks = async (date, works) => {
    try {
      await api.post("/settings/holidays/national-override", { date, works });
      setNationals(prev =>
        prev.map(h => (h.date === date ? { ...h, works } : h))
      );
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setDialog({ mode: "add" });
  };

  const openEdit = idx => {
    const h = customs[idx] || {};
    setForm({
      date: h.date || "",
      name: h.name || "",
      recurring: !!h.recurring,
      scope: h.scope || "",
      message: h.message || "",
    });
    setDialog({ mode: "edit", idx });
  };

  const openNational = h => {
    setForm({ ...EMPTY_FORM, date: h.date, name: h.name, scope: h.scope || "", message: h.message || "" });
    setDialog({ mode: "national", date: h.date, name: h.name });
  };

  const save = async () => {
    try {
      if (dialog.mode === "add") {
        if (!form.date) return;
        await api.post("/settings/holidays/custom", form);
      } else if (dialog.mode === "edit") {
        await api.put(`/settings/holidays/custom/${dialog.idx}`, form);
      } else {
        await api.post("/settings/holidays/national-note", {
          date: dialog.date,
          scope: form.scope,
          message: form.message,
        });
      }
      toast.success(i18n.t("holidays.saveOk"));
      setDialog(null);
      load();
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const removeCustom = async idx => {
    if (!window.confirm(i18n.t("holidays.confirmDelete"))) return;
    try {
      await api.delete(`/settings/holidays/custom/${idx}`);
      load();
      changed();
    } catch (err) {
      toastError(err);
    }
  };

  const extras = h => (
    <>
      {h.scope && <span className={classes.scope}>{h.scope}</span>}
      {h.message && (
        <Tooltip title={h.message}>
          <ChatBubbleOutlineIcon
            style={{ fontSize: 14, marginLeft: 6, verticalAlign: "middle", opacity: 0.7 }}
            aria-label={i18n.t("holidays.hasMessage")}
          />
        </Tooltip>
      )}
    </>
  );

  const isCustomDialog = dialog && dialog.mode !== "national";

  return (
    <div>
      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("holidays.nationals")} — {year}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        {i18n.t("holidays.nationalsHelp")}
      </Typography>
      <div className={classes.list}>
        {nationals.map(h => (
          <div key={h.date} className={classes.row}>
            <span className={classes.rowMain}>
              <span className={classes.rowName}>
                {fmtDate(h.date)} — {h.name}
              </span>
              {!h.works && extras(h)}
            </span>
            <span className={classes.rowActions}>
              {!h.works && (
                <Tooltip title={i18n.t("holidays.editNote")}>
                  <IconButton
                    size="small"
                    onClick={() => openNational(h)}
                    aria-label={i18n.t("holidays.editNote")}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Typography variant="caption" color="textSecondary">
                {h.works ? i18n.t("holidays.works") : i18n.t("holidays.closed")}
              </Typography>
              <Switch
                size="small"
                color="primary"
                checked={!!h.works}
                onChange={e => toggleNationalWorks(h.date, e.target.checked)}
                inputProps={{ "aria-label": `${h.name} — ${i18n.t("holidays.works")}` }}
              />
            </span>
          </div>
        ))}
      </div>

      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("holidays.customs")}
      </Typography>
      {customs.length === 0 && (
        <Typography variant="caption" color="textSecondary" display="block">
          {i18n.t("holidays.noCustoms")}
        </Typography>
      )}
      {customs.map((h, idx) => (
        <div key={idx} className={classes.row}>
          <span className={classes.rowMain}>
            <span className={classes.rowName}>
              {fmtDate(h.date)} — {h.name}
              {h.recurring && ` (${i18n.t("holidays.recurring")})`}
            </span>
            {extras(h)}
          </span>
          <span className={classes.rowActions}>
            <IconButton size="small" onClick={() => openEdit(idx)} aria-label={i18n.t("holidays.edit")}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => removeCustom(idx)} aria-label={i18n.t("holidays.delete")}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </div>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={openAdd}
        style={{ textTransform: "none", marginTop: 8 }}
        disabled={loading}
      >
        {i18n.t("holidays.addCustom")}
      </Button>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.mode === "add"
            ? i18n.t("holidays.addCustom")
            : dialog?.mode === "edit"
            ? i18n.t("holidays.editTitle")
            : `${fmtDate(dialog?.date)} — ${dialog?.name || ""}`}
        </DialogTitle>
        <DialogContent>
          {isCustomDialog && (
            <>
              <TextField
                type="date"
                label={i18n.t("holidays.dateLabel")}
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
              />
              <TextField
                label={i18n.t("holidays.nameLabel")}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                helperText={i18n.t("holidays.nameHelp")}
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                inputProps={{ maxLength: 200 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.recurring}
                    onChange={e => setForm({ ...form, recurring: e.target.checked })}
                    color="primary"
                  />
                }
                label={i18n.t("holidays.recurringLabel")}
              />
            </>
          )}
          {dialog?.mode === "national" && (
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              {i18n.t("holidays.nationalNoteHelp")}
            </Typography>
          )}
          <TextField
            label={i18n.t("holidays.scopeLabel")}
            value={form.scope}
            onChange={e => setForm({ ...form, scope: e.target.value })}
            helperText={i18n.t("holidays.scopeHelp")}
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label={i18n.t("holidays.messageLabel")}
            value={form.message}
            onChange={e => setForm({ ...form, message: e.target.value })}
            helperText={i18n.t("holidays.messageHelp")}
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
            multiline
            rows={3}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{i18n.t("holidays.cancel")}</Button>
          <Button
            onClick={save}
            color="primary"
            disabled={isCustomDialog && !form.date}
          >
            {i18n.t("holidays.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HolidaysEditor;
